import { useCallback, useState, type ReactNode } from 'react';
import { ShareFileModal, type ShareDraft, type ShareRoute } from '../../components/files/ShareFileModal';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { t } from '@bms2/shared';
import { FileDetailsModal, type FileDetailsRow } from './FileDetailsModal';
import { ReleaseFileModal } from './DealModals';
import type { ReleaseCompanies } from './releaseFiles';
import { PAGES_PATH, PICKER_CAP, apiFetch, post, useApi, FILES_PATH } from './api';

/**
 * 🔒 Share and File details on a file row outside a record page's tile canvas
 * (decision 3010): a document's Files block opens the same two windows a Files
 * tile's row opens, over the same routes. Each window is keyed to the file it
 * opens on and unmounted when it closes, so a second row never shows the
 * first's draft.
 *
 * 🔒 Decision 11480: where the document's kind offers Release, its row opens
 * the one Release window a Deal's file row opens, over the companies handed in.
 */
export function useFileRowModals(apiBaseUrl: string, onChanged: () => void, releaseTo: ReleaseCompanies | null = null): { share: (file: { id: string; name: string }) => void; details: (file: FileDetailsRow) => void; release: (file: { id: string; name: string }) => void; modals: ReactNode } {
  const [sharing, setSharing] = useState<{ id: string; name: string } | null>(null);
  const [detailing, setDetailing] = useState<FileDetailsRow | null>(null);
  const [releasing, setReleasing] = useState<{ id: string; name: string } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [released, setReleased] = useState<string | null>(null);
  /** 🔒 The recipient picker asks the people list with the typed words, as every person picker does (T539). */
  const searchPeople = useCallback(async (query: string, signal: AbortSignal) => {
    const response = await apiFetch(`${apiBaseUrl}${PAGES_PATH}/list/PersonRecord?${PICKER_CAP}&q=${encodeURIComponent(query)}`, { signal });
    if (!response.ok) return [];
    const body = (await response.json()) as { rows: { id: string; fullName: string; workEmail: string | null }[] };
    return body.rows.map((person) => ({ id: person.id, name: person.fullName, email: person.workEmail }));
  }, [apiBaseUrl]);
  const shareDetail = useApi<{ file: { confidentialityRoleId: string | null } }>(sharing ? `${FILES_PATH}/${sharing.id}` : null, apiBaseUrl);
  const modals = (
    <>
      {notice ? <AlertBanner tone="error" message={notice} action={{ label: t('modal.dismiss'), onAction: () => setNotice(null) }} /> : null}
      {released ? <AlertBanner tone="good" message={released} action={{ label: t('modal.dismiss'), onAction: () => setReleased(null) }} /> : null}
      {sharing ? (
        <ShareFileModal
          key={`share-${sharing.id}`}
          open
          file={{ id: sharing.id, filename: sharing.name, confidential: shareDetail.state.kind === 'ready' ? shareDetail.state.data.file.confidentialityRoleId !== null : false }}
          searchPeople={searchPeople}
          onDeliver={async (route: ShareRoute, draft: ShareDraft) => {
            const delivered = await post<{ url: string; qrDataUrl?: string }>(`${FILES_PATH}/${sharing.id}/share`, { route, permission: draft.permission, recipientPersonId: draft.recipientPersonId, expiresAt: draft.expiresAt, email: draft.email }, apiBaseUrl);
            if (!delivered.ok) {
              setNotice(delivered.message);
              throw new Error(delivered.message);
            }
            return delivered.data;
          }}
          onCancel={() => setSharing(null)}
        />
      ) : null}
      {releasing && releaseTo ? (
        <ReleaseFileModal
          key={`release-${releasing.id}`}
          open
          fileId={releasing.id}
          fileName={releasing.name}
          companies={releaseTo.companies}
          customerCompanyId={releaseTo.customerCompanyId}
          noTargets={t('files.release.noTargets')}
          apiBaseUrl={apiBaseUrl}
          onCancel={() => setReleasing(null)}
          onSaved={(message) => { setReleased(message); onChanged(); }}
        />
      ) : null}
      {detailing ? <FileDetailsModal key={`file-${detailing.id}`} open file={detailing} apiBaseUrl={apiBaseUrl} onCancel={() => setDetailing(null)} onSaved={onChanged} /> : null}
    </>
  );
  return { share: setSharing, details: setDetailing, release: setReleasing, modals };
}
